import {
  Button,
  Col,
  DatePicker,
  Input,
  message,
  Row,
  Select,
  Spin,
  Table,
  Tag,
} from 'antd';
import moment from 'moment';
import React, { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import styled from 'styled-components';

import { fetchAPI } from '../../utils/api';

const InvoiceConsolidation = () => {
  const regions = useSelector((state: AppState) => state?.user?.regions);
  const [loading, setLoading] = useState<'BUYER_ENTITY' | 'CONS_DATA'>(null);
  const [consolidationData, setConsolidationData] = useState([]);
  const [buyerEntities, setBuyerEntities] = useState([]);
  const [selectedEntity, setSelectedEntity] = useState(null);
  const [selectedRegion, setSelectedRegion] = useState('SG');
  const [dates, setDates] = useState({
    start: moment().subtract(12, 'month'),
    end: moment(),
  });

  const generateInvoice = async () => {
    try {
      const pdfBlob = await fetchAPI('entity/buyer/consolidate/generate_pdf', {
        method: 'POST',
        payload: {
          from_date: dates?.start.format('YYYY-MM-DD') ?? undefined,
          to_date: dates?.end.format('YYYY-MM-DD') ?? undefined,
          entity_buyer_id: selectedEntity,
        },
      });
      const anchor = document.createElement('a');
      anchor.setAttribute('href', URL.createObjectURL(pdfBlob));
      anchor.setAttribute(
        'download',
        `consolidation_invoice_${selectedEntity}.pdf`
      );
      document.body.appendChild(anchor); // required for firefox
      anchor.click();
      document.body.removeChild(anchor);
    } catch (error) {
      message.error(error?.message);
    }
  };

  const downloadFile = async () => {
    try {
      const blob = await fetchAPI('entity/buyer/consolidate/export', {
        method: 'POST',
        payload: {
          from_date: dates?.start.format('YYYY-MM-DD') ?? undefined,
          to_date: dates?.end.format('YYYY-MM-DD') ?? undefined,
          entity_buyer_id: selectedEntity,
        },
      });

      const anchor = document.createElement('a');
      anchor.setAttribute('href', URL.createObjectURL(blob));
      anchor.setAttribute('download', `consolidation_${selectedEntity}.xlsx`);
      document.body.appendChild(anchor); // required for firefox
      anchor.click();
      document.body.removeChild(anchor);
    } catch (error) {}
  };

  const getConsolidationData = async () => {
    if (!selectedEntity) {
      message.warning('Please select a entity');
      return;
    }
    try {
      setLoading('CONS_DATA');
      const res = await fetchAPI('entity/buyer/consolidate', {
        method: 'GET',
        payload: {
          from_date: dates?.start.format('YYYY-MM-DD') ?? undefined,
          to_date: dates?.end.format('YYYY-MM-DD') ?? undefined,
          entity_buyer_id: selectedEntity,
        },
      });
      setConsolidationData(res?.orders ?? []);
    } catch (error) {
    } finally {
      setLoading(null);
    }
  };

  const getBuyerEntities = async (prev = [], page = 1) => {
    try {
      setLoading('BUYER_ENTITY');
      const resp = await fetchAPI('entity/buyer/list', {
        method: 'GET',
        payload: {
          page,
          per_page: 100,
          region: selectedRegion ?? 'SG',
          q: undefined,
        },
      });
      const allItems = [...prev, ...resp.items];
      if (resp.page * resp.per_page < resp.total) {
        return getBuyerEntities(allItems, page + 1);
      } else {
        setBuyerEntities(allItems);
      }
    } catch (error) {
    } finally {
      setTimeout(() => {
        setLoading(null);
      }, 1000);
    }
  };

  useEffect(() => {
    getBuyerEntities();
  }, [selectedRegion]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (selectedEntity) {
      getConsolidationData();
    }
  }, [selectedEntity, dates]); // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <Wrapper>
      <Spin spinning={loading ? true : false}>
        <div style={{ display: 'flex', alignItems: 'center' }}>
          <div>
            <Input.Group>
              <Row>
                <Col>
                  <Select
                    style={{ width: '170px', marginRight: '10px' }}
                    placeholder="Select Buyer Name"
                    showSearch
                    filterOption={(input, option) => {
                      const res = String(option?.children ?? '')
                        .toLowerCase()
                        .includes(input?.toLowerCase() ?? '');
                      return res ?? false;
                    }}
                    onChange={item => {
                      setSelectedEntity(item);
                    }}
                    value={selectedEntity}
                  >
                    {buyerEntities?.map((item, index) => (
                      <Select.Option key={item?.id} value={item?.id}>
                        {item?.name}
                      </Select.Option>
                    ))}
                  </Select>
                </Col>
                <Col>
                  <Select
                    style={{ width: '80px', marginRight: '10px' }}
                    placeholder="Region"
                    onChange={val => {
                      setSelectedRegion(String(val));
                      setSelectedEntity(null);
                      setConsolidationData([]);
                    }}
                    value={selectedRegion}
                  >
                    {regions?.map(region => (
                      <Select.Option key={region} value={region}>
                        {region}
                      </Select.Option>
                    ))}
                  </Select>
                </Col>
              </Row>
            </Input.Group>
          </div>

          <div style={{ flexGrow: 1 }}>
            <DatePicker.RangePicker
              style={{ marginRight: '10px' }}
              value={[dates?.start, dates?.end]}
              format={'DD MMM YYYY'}
              onChange={newDates =>
                setDates({
                  start: newDates?.[0] ?? undefined,
                  end: newDates?.[1] ?? undefined,
                })
              }
            />
          </div>

          {consolidationData?.length > 0 && (
            <Button
              style={{ marginRight: '10px' }}
              onClick={() => downloadFile()}
            >
              Download
            </Button>
          )}

          {consolidationData?.length > 0 && (
            <Button onClick={() => generateInvoice()}>Generate Invoice</Button>
          )}
        </div>
        {consolidationData?.length > 0 ? (
          <div style={{ marginTop: '20px' }}>
            <Table
              pagination={false}
              dataSource={(consolidationData || [])?.map((item, index) => ({
                ...item,
                sn: index + 1,
              }))}
              columns={[
                {
                  title: 'No.',
                  key: 'sn',
                  dataIndex: 'sn',
                },
                {
                  title: 'SAID',
                  key: 'said',
                  render: record => {
                    return <div>{record?.said}</div>;
                  },
                },
                {
                  title: 'SPID',
                  key: 'spid',
                  render: record => {
                    return (
                      <div>
                        {record?.spid?.map(spid => (
                          <>
                            <Tag>{spid}</Tag>
                          </>
                        ))}
                      </div>
                    );
                  },
                },
                {
                  title: 'Po Ref. No.',
                  key: 'po_reference_number',
                  render: record => {
                    return <div>{record?.po_reference_number}</div>;
                  },
                },
                {
                  title: 'Amount',
                  key: 'amount',
                  render: record => {
                    return <div>{Number(record?.amount).toFixed(2)}</div>;
                  },
                },
                {
                  title: 'Status',
                  key: 'status',
                  render: record => {
                    return <div>{record?.status}</div>;
                  },
                },
              ]}
              scroll={{ x: 500, y: 550 }}
            />
          </div>
        ) : (
          <div style={{ padding: '10% 20px', textAlign: 'center' }}>
            {selectedEntity ? 'Data Not Found' : 'Please select a Entity'}
          </div>
        )}
      </Spin>
    </Wrapper>
  );
};

const Wrapper = styled.div``;
export default InvoiceConsolidation;
